import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Option } from '@rivethealth/data/options';
import { ObservableInput } from 'observable-input';
import {
  BehaviorSubject,
  combineLatest,
  merge,
  Observable,
  Subject,
} from 'rxjs';
import {
  filter,
  map,
  startWith,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AnchoredPosition } from '../callout/callout-position';
import { Loadable, success } from '../helpers/loadable';
import {
  findOption,
  getOptionDisplay,
  getOptionDisplayFromOptions,
} from './options';
import { SingleSelectNodeDirective } from './single-select-node.directive';
import { SingleSelectComponent } from './single-select.component';

/**
 * @deprecated Use riv-single-select instead.
 * @see https://rivethealth.github.io/design-system/?path=/story/lib-input-single-select--example
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'deprecated-single-select-dropdown',
  host: { '[class.inline]': 'hostInline' },
  styleUrls: ['./single-select-dropdown.component.scss'],
  templateUrl: './single-select-dropdown.component.html',
})
export class DeprecatedSingleSelectDropdownComponent<T = unknown>
  implements OnInit
{
  private readonly destroy = new Subject();

  @Input() error = false;
  @Input() warning = false;

  @ViewChild('dropdown', { static: false })
  dropdownRef: ElementRef<HTMLElement>;

  @ViewChild(SingleSelectComponent, { static: false })
  singleSelect: SingleSelectComponent;

  @ViewChild('callout', { static: false }) calloutRef: ElementRef<HTMLElement>;

  isStringArray(value: any): value is string[] {
    return Array.isArray(value) && value.every(v => typeof v === 'string');
  }

  @ContentChild(SingleSelectNodeDirective, { static: false })
  nodeDirective: SingleSelectNodeDirective<T>;

  @ContentChild('customDropdown')
  dropdownTemplate: TemplateRef<any>;

  hasCustomDropdown(): boolean {
    return !!this.dropdownTemplate;
  }

  @ObservableInput()
  @Input('options')
  private readonly _options!: Observable<Option[] | string[]>;
  defaultedOptions: Observable<Option[]> = this._options.pipe(
    startWith([]),
    map(options => {
      if (this.isStringArray(options)) {
        return (<string[]>options).map(option => ({
          id: option,
          title: option,
          subtitle: null,
        }));
      }
      return options;
    }),
  );

  @ObservableInput()
  @Input()
  optionsLoadable!: Observable<Loadable<{ options: Option[]; count: number }>>;

  readonly options = merge(
    this.defaultedOptions,
    this.optionsLoadable.pipe(
      filter(a => !!a),
      success(),
      map(options => options.options),
    ),
  );

  @ObservableInput()
  @Input('disabled')
  private readonly _disabled!: Observable<boolean>;
  defaultedDisabled = this._disabled.pipe(startWith(false));

  @ObservableInput()
  @Input('inline')
  private readonly _inline!: Observable<boolean>;
  defaultedInline = this._inline.pipe(startWith(false));

  @ObservableInput()
  @Input('required')
  private readonly _required!: Observable<boolean>;
  defaultedRequired = this._required.pipe(startWith(false));

  @ObservableInput()
  @Input('exact')
  private readonly _exact!: Observable<boolean>;
  defaultedExact = this._exact.pipe(startWith(false));

  @ObservableInput()
  @Input('filterable')
  private readonly _filterable!: Observable<boolean>;
  defaultedFilterable: Observable<boolean> = this._filterable.pipe(
    startWith(false),
  );

  @ObservableInput()
  @Input('placeholder')
  private readonly _placeholder!: Observable<string | null>;
  defaultedPlaceholder: Observable<string | null> = this._placeholder.pipe(
    startWith(<string | null>null),
  );

  @ObservableInput()
  @Input('loadable')
  private readonly _loadable!: Observable<Loadable<any> | null>;
  defaultedLoadable = this._loadable.pipe(startWith(null));

  // supports selected option as an id or the option object
  // only one of selectedOptionId and selectedOption should be used
  // if selectedOption is undefined we assume we are in id mode
  // if selectedOption is null we assume we are in option object mode
  // and the value is just null. this is why they can be null | undefined
  @ObservableInput()
  @Input('selectedOptionId')
  private _selectedOptionId!: Observable<string | null | undefined>;
  defaultedSelectedOptionId = this._selectedOptionId.pipe(startWith(undefined));

  @ObservableInput()
  @Input('selectedOption')
  private _selectedOption!: Observable<Option | null | undefined>;
  defaultedSelectedOption = this._selectedOption.pipe(startWith(undefined));

  @Input()
  autoFocus: boolean = false;

  @Input()
  displayId: boolean;

  @Input()
  width: number | null = null;

  @Input()
  scrollable: boolean = true;

  @Input()
  maxElements: number;

  get dropdownWidth() {
    return this.width ?? this.dropdownRef.nativeElement.offsetWidth;
  }

  @Output() readonly filter = new Subject<string>();

  selectedDisplay = combineLatest([
    this.defaultedSelectedOption,
    this.defaultedSelectedOptionId,
    this.options,
  ]).pipe(
    map(([selected, selectedId, options]) => {
      if (selected !== undefined) {
        return getOptionDisplay(selected!, this.displayId);
      } else {
        return selectedId && options
          ? getOptionDisplayFromOptions(selectedId, options, this.displayId)
          : '';
      }
    }),
  );

  selectedId = combineLatest([
    this.defaultedSelectedOption,
    this.defaultedSelectedOptionId,
  ]).pipe(
    map(([selected, selectedId]) => {
      if (selected !== undefined) {
        return selected ? selected.id : '';
      } else {
        return selectedId || '';
      }
    }),
  );

  @Output()
  readonly selectedOptionIdChange = new Subject<string>();

  readonly selectedOptionChangeSideEffects = this.selectedOptionIdChange.pipe(
    tap(() => this.close()),
  );

  @Output()
  readonly selectedOptionChange = this.selectedOptionIdChange.pipe(
    withLatestFrom(this.defaultedSelectedOption, this.options),
    filter(([_, selectedOption]) => selectedOption !== undefined),
    map(([selectedId, selectedOption, options]) => {
      return findOption(selectedId, options);
    }),
  );

  readonly doOpen = new BehaviorSubject<boolean>(false);

  @ObservableInput()
  @Input('dismiss')
  private readonly dismiss!: Observable<number>;

  readonly openSubject = merge(
    this.doOpen,
    this.dismiss.pipe(
      map(() => {
        return false;
      }),
    ),
  );

  public close() {
    this.doOpen.next(false);
  }

  public open() {
    this.doOpen.next(true);
    this.scrollToSelectedOption();
  }

  private scrollToSelectedOption() {
    if (this.singleSelect) {
      this.singleSelect.scrollSelectedIntoView();
    } else {
      setTimeout(() => {
        if (this.singleSelect) {
          this.singleSelect.scrollSelectedIntoView();
        }
      }, 0);
    }
  }

  isOpen = false;

  private readonly openSideEffects = this.openSubject.pipe(
    tap(isOpen => {
      if (!isOpen) {
        if (this.dropdownRef) {
          this.dropdownRef.nativeElement.focus();
        }
        this.filter.next('');
      }
      this.isOpen = isOpen;
    }),
  );

  readonly position = new BehaviorSubject<AnchoredPosition | null>(null);

  readonly positionedTop = this.position.pipe(
    map(position => {
      return (
        position === AnchoredPosition.TOP_RIGHT ||
        position === AnchoredPosition.TOP_LEFT
      );
    }),
  );

  readonly topAndBottomPositions = [
    AnchoredPosition.BOTTOM_RIGHT,
    AnchoredPosition.BOTTOM_LEFT,
    AnchoredPosition.TOP_RIGHT,
    AnchoredPosition.TOP_LEFT,
  ];

  public onCalloutFocusOut() {
    this.close();
  }

  private forwardKeyPressToOptions($event: KeyboardEvent) {
    if (this.singleSelect) {
      this.singleSelect.externalKeyPress.next($event.key);
    } else {
      setTimeout(() => {
        if (this.singleSelect) {
          this.singleSelect.externalKeyPress.next($event.key);
        }
      }, 0);
    }
  }

  @HostListener('keyup', ['$event'])
  @HostListener('keydown', ['$event'])
  keyUp($event: any) {
    const key = $event.key;
    if (
      key === 'ArrowDown' ||
      key === 'ArrowUp' ||
      key === 'Enter' ||
      key === ' '
    ) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter') {
      if ($event.type === 'keydown') {
        if (this.isOpen === true) {
          this.forwardKeyPressToOptions($event);
        } else {
          this.open();
        }
      }
    }
    if (key.length === 1) {
      if ($event.type === 'keydown') {
        this.open();
        this.forwardKeyPressToOptions($event);
      }
    }
    if (key === 'Escape') {
      if ($event.type === 'keydown') {
        this.close();
      }
    }
    if (key === 'Tab') {
      if ($event.type === 'keydown') {
        if (this.isOpen === true) {
          this.forwardKeyPressToOptions($event);
        }
      }
    }
  }

  hostInline: boolean = false;

  ngOnInit() {
    this.openSideEffects.pipe(takeUntil(this.destroy)).subscribe();
    this.selectedOptionChangeSideEffects
      .pipe(takeUntil(this.destroy))
      .subscribe();
    this.defaultedInline
      .pipe(takeUntil(this.destroy))
      .subscribe(inline => (this.hostInline = inline));
  }

  ngOnDestroy() {
    this.destroy.next(undefined);
  }
}
